import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
	useTheme,
} from '@mui/material';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useLocation, useParams } from 'react-router-dom';
import { useGetListByParamsQuery } from '@/api/common';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { RoleType } from '@/api/enum/userRole.enum';
import { useSubmitToICMutation } from '@/api/member-management/admissionRequest';

interface Props {
	open: boolean;
	onClose: () => void;
}

const SubmitToICDialog = (props: Props) => {
	const { open, onClose } = props;
	const { t } = useTranslation();
	const [submitToIC] = useSubmitToICMutation();
	const { admissionRequestId } = useParams();
	const location = useLocation();
	const memberType = i18n.t(
		location.pathname.includes('admission-request-list')
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);

	const userInfo = useSelector(selectMyInfo);

	const { data: staffListByRole }: any = useGetListByParamsQuery(
		{
			branchId: userInfo?.currentBranch.id,
			roleType: RoleType.OIC_AOIC_CL,
		},
		{ refetchOnMountOrArgChange: true, skip: !userInfo?.currentBranch && !open }
	);

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		actionRequiredBy: Yup.string().required(t('errorMessage.required')),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const handleClose = () => {
		methods.reset({});
		onClose();
	};
	const onSubmit: SubmitHandler<any> = async (data) => {
		const response = await submitToIC({
			...data,
			id: admissionRequestId,
		});
		if ('data' in response) {
			enqueueSnackbar(
				t('snackbar.updateSubmitToICSuccess', { type: memberType }),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			handleClose();
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
				<DialogTitle variant='h5'>{t('button.submitToIC')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('member.submitToICText')}
					</Typography>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid
								container
								spacing={2}
								marginTop={0}
								marginBottom={2}
								alignItems='center'
							>
								<Grid size={12}>
									<AppDropDownMenu
										control={methods.control}
										name='actionRequiredBy'
										label={t('member.QICAOICCL') + '*'}
										options={staffListByRole?.data || []}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SubmitToICDialog;
